import type { OrderPartialCancellation } from '@skupreme/api-contracts/core';
import { Badge, Tooltip, cn } from '@skupreme/shared/ui';
import {
  getCanceledLines,
  PARTIALLY_CANCELED_LABEL,
  summarizeCanceledLines,
} from '../../lib/partial-cancellation';

export type PartiallyCanceledBadgeProps = {
  cancellation: OrderPartialCancellation | undefined;
  /** The list row's chip size; the detail card uses the default badge. */
  compact?: boolean;
  className?: string;
};

/**
 * Information, not an issue: a partly canceled order is legitimate and its
 * open lines ship as they are, so this is an info badge beside the status
 * rather than an attention chip, and nothing waits on it. The hover lists the
 * removed lines so the operator knows what the customer no longer expects.
 */
export function PartiallyCanceledBadge({
  cancellation,
  compact,
  className,
}: PartiallyCanceledBadgeProps) {
  const lines = getCanceledLines(cancellation);

  if (lines.length === 0) {
    return null;
  }

  return (
    <Tooltip
      placement="bottom start"
      content={
        <span className="sk:flex sk:flex-col sk:gap-1">
          <span className="sk:text-fg-tertiary">
            {summarizeCanceledLines(lines)}. The other lines ship as they are.
          </span>
          {lines.map(line => (
            <span key={line.key} className="sk:flex sk:flex-col">
              <span>
                {line.title}
                {line.sku && line.sku !== line.title ? ` · ${line.sku}` : ''}
                {` · ${line.qty} canceled`}
              </span>
              {line.note ? (
                <span className="sk:text-fg-tertiary">{line.note}</span>
              ) : null}
            </span>
          ))}
        </span>
      }
    >
      <span
        className={cn('sk:inline-flex sk:min-w-0 sk:cursor-default', className)}
      >
        <Badge
          tone="info"
          className={cn(
            'sk:min-w-0',
            compact && 'sk:max-w-60 sk:text-xs sk:font-normal',
          )}
        >
          <span className="sk:min-w-0 sk:truncate">
            {PARTIALLY_CANCELED_LABEL}
          </span>
        </Badge>
      </span>
    </Tooltip>
  );
}
